/**
 * Payload sanitization for tax calculator APIs.
 *
 * Nullifies quantity fields in location year data that are forbidden
 * for the selected business type. This prevents validation errors
 * when stale field values remain in boundData after a type change.
 *
 * Field rules mirror the backend validation in:
 *   tax_calculator/apps/core/lib/core/tax_calculation_location.ex
 *   tax_calculator/apps/core/lib/core/tax_tables_common.ex
 */

const ALL_QUANTITY_FIELDS = [
  "employees",
  "part_time_employees",
  "units",
  "square_feet",
  "mobile_units"
];

/**
 * Maps UI business type strings to their allowed quantity fields.
 * Any quantity field NOT in the allowed list is forbidden and will be nullified.
 */
const ALLOWED_FIELDS_BY_TYPE = {
  "Standard Business": ["employees", "part_time_employees"],
  "Self-Employed (1099 Contractor)": ["employees", "part_time_employees"],
  "Residential Property": ["units"],
  "Residential Landlord": ["units"],
  "Commercial Property": ["square_feet"],
  "Commercial Landlord": ["square_feet"],
  "Mixed-Use Landlord (Residential and Commercial)": ["square_feet", "units"],
  "Mobile Home Park": ["mobile_units"]
};

/** Sanitization only applies to these endpoints. */
const TAX_CALCULATOR_PATHS = ["/api/taxes", "/api/business_registrations"];

const isDirectPayload = payload => Boolean(payload.type && payload.locations);

const isWrappedPayload = payload =>
  Boolean(payload.inbound_request?.type && payload.inbound_request?.locations);

/**
 * Checks if a URL targets a tax-calculator endpoint.
 */
export const isTaxCalculatorEndpoint = url => {
  if (!url) return false;
  try {
    const { pathname } = new URL(url);
    return TAX_CALCULATOR_PATHS.some(path => pathname.endsWith(path));
  } catch {
    return TAX_CALCULATOR_PATHS.some(path => url.includes(path));
  }
};

/**
 * Returns the list of forbidden quantity fields for a given business type.
 * Returns empty array if type is unknown (no sanitization needed).
 */
export const getForbiddenFields = type => {
  const allowed = ALLOWED_FIELDS_BY_TYPE[type];
  if (!allowed) return [];
  return ALL_QUANTITY_FIELDS.filter(field => !allowed.includes(field));
};

/**
 * Nullifies forbidden quantity fields in a single year data object.
 */
const nullifyForbiddenFields = (yearData, forbiddenFields) => {
  if (!yearData || typeof yearData !== "object") return yearData;

  const result = { ...yearData };
  for (const field of forbiddenFields) {
    if (field in result && result[field] !== null) {
      result[field] = null;
    }
  }
  return result;
};

/**
 * Nullifies forbidden quantity fields across all locations and their year entries.
 */
const sanitizeLocations = data => {
  const forbiddenFields = getForbiddenFields(data.type);
  if (forbiddenFields.length === 0) return data;

  const sanitizedLocations = Object.fromEntries(
    Object.entries(data.locations).map(([locationKey, locationData]) => {
      if (!locationData?.years || typeof locationData.years !== "object") {
        return [locationKey, locationData];
      }

      const updatedYears = Object.fromEntries(
        Object.entries(locationData.years).map(([year, yearData]) => [
          year,
          nullifyForbiddenFields(yearData, forbiddenFields)
        ])
      );

      return [locationKey, { ...locationData, years: updatedYears }];
    })
  );

  return { ...data, locations: sanitizedLocations };
};

/**
 * Sanitizes location quantity fields in a payload based on business type.
 *
 * Supported payload structures:
 *   - Direct:  { type, locations }              → tax calculation
 *   - Wrapped: { inbound_request: { type, locations } } → business registration
 *
 * Returns original payload unchanged if no sanitization is needed.
 */
export const sanitizePayload = payload => {
  if (!payload || typeof payload !== "object") return payload;

  if (isDirectPayload(payload)) {
    return sanitizeLocations(payload);
  }

  if (isWrappedPayload(payload)) {
    return {
      ...payload,
      inbound_request: sanitizeLocations(payload.inbound_request)
    };
  }

  return payload;
};
